import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, BadgeCheck, Sparkles, Gem, Landmark, Hammer, Pencil, Gift, Star, Instagram, ArrowUpRight } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import CursorSpot from "@/components/CursorSpot";
import InstagramEmbed from "@/components/InstagramEmbed";
import { MaskedWords, MaskedLines, Reveal } from "@/components/Motion";
import Seo from "@/components/Seo";
import { useProducts } from "@/context/ProductsContext";
import { CATEGORIES, TESTIMONIALS, WHY_CHOOSE, BLOG_POSTS, IMG, BRAND } from "@/data/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { ShieldCheck, BadgeCheck, Sparkles, Gem, Landmark, Hammer, Pencil, Gift };

// Intro overlay lasts ~1.9s; delay hero copy so it enters after.
const HERO_DELAY = 2.05;

const MANIFESTO = [
  { n: "01", title: "Purity is not negotiable.", body: "Only 925 sterling and 999 fine silver. Every piece BIS hallmarked and independently assayed. The mark is not decoration — it is a promise you can weigh." },
  { n: "02", title: "Craft outlives the trend.", body: "Karigars trained across three generations shape each piece by hand. What comes out of our workshop in Pune is meant to be worn, borrowed, and passed down — not replaced." },
  { n: "03", title: "Beauty is quiet.", body: "We design in silhouettes, not slogans. Nothing shouts. Everything holds. The kind of silver you notice on the second glance, and remember on the third." },
];

export default function Home() {
  const { products } = useProducts();
  const featured = (products.filter(p => p.featured).length ? products.filter(p => p.featured) : products).slice(0, 8);
  const [igPosts, setIgPosts] = useState([]);
  const heroRef = useRef(null);
  const spotRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/ig-posts`).then((r) => setIgPosts(r.data || [])).catch(() => {});
  }, []);

  // Parallax image on hero
  const { scrollYProgress: heroSY } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(heroSY, [0, 1], [0, 140]);
  const heroImgScale = useTransform(heroSY, [0, 1], [1, 1.08]);
  const heroCopyY = useTransform(heroSY, [0, 1], [0, -60]);

  return (
    <div data-testid="page-home">
      {/* HERO — kinetic, masked reveals, cursor spotlight, parallax image */}
      <section ref={heroRef} className="relative min-h-[100svh] pt-28 lg:pt-32 pb-16 overflow-hidden">
        {/* soft ambient background */}
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(192,192,192,0.20),transparent_70%)]" />
        {/* cursor spot host */}
        <div ref={spotRef} className="absolute inset-0 overflow-hidden">
          <CursorSpot containerRef={spotRef} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10">
          {/* top rail */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: HERO_DELAY, duration: 0.6 }}
            className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]"
          >
            <span>SIRIJA · Est. Raviwar Peth · Pune</span>
            <span className="hidden sm:block">Capsule N° 07 — Vivaha</span>
            <span className="hidden md:block">{BRAND.website}</span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end mt-14 lg:mt-16">
            {/* Left copy */}
            <motion.div style={{ y: heroCopyY }} className="lg:col-span-7">
              <h1 className="font-serif font-light text-[48px] sm:text-[80px] lg:text-[104px] leading-[0.94] tracking-[-0.03em] text-[#1A1A1A]">
                <MaskedLines
                  delayBase={HERO_DELAY}
                  lines={[
                    <span key="a">Celebrate every</span>,
                    <span key="b">moment in</span>,
                    <span key="c" className="italic silver-shimmer">pure silver.</span>,
                  ]}
                />
              </h1>

              <div className="mt-10 max-w-lg">
                <MaskedWords
                  delayBase={HERO_DELAY + 0.5}
                  stagger={0.02}
                  className="block text-[15px] lg:text-base leading-relaxed text-[#2E2E2E]/75 font-light"
                  text="Handcrafted silver, made in Pune. Bridal, festive, daily, pooja and gifting — assayed, hallmarked, and quietly guaranteed."
                />
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: HERO_DELAY + 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="mt-12 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/collections"
                  data-testid="hero-explore-btn"
                  className="btn-fill group relative h-12 px-7 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.22em] flex items-center gap-2 hover:text-white"
                >
                  Explore Collection
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-visit-btn"
                  className="h-12 px-7 rounded-full border border-[#1A1A1A]/15 text-xs uppercase tracking-[0.22em] flex items-center hover:bg-[#F8F9FA] transition-colors"
                >
                  Visit Store
                </Link>
              </motion.div>

              {/* Metrics */}
              <motion.div
                initial="hidden" animate="show"
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: HERO_DELAY + 1.15 } } }}
                className="mt-16 grid grid-cols-3 max-w-md gap-8"
              >
                {[
                  { k: "925", v: "Sterling purity" },
                  { k: "BIS", v: "Hallmarked" },
                  { k: "Pune", v: "Since years" },
                ].map((m) => (
                  <motion.div key={m.k} variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } } }}>
                    <div className="font-serif text-3xl lg:text-4xl">{m.k}</div>
                    <div className="mt-1 overline text-[#A0A0A0]">{m.v}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right image w/ parallax + clipped reveal */}
            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{ clipPath: "inset(0 0 0% 0)" }}
                transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: HERO_DELAY - 0.1 }}
                className="relative aspect-[4/5] rounded-[24px] overflow-hidden ring-1 ring-black/5"
              >
                <motion.img
                  src={IMG.bride}
                  alt="Silver bridal capsule"
                  style={{ y: heroImgY, scale: heroImgScale }}
                  className="absolute inset-0 h-[110%] w-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute left-6 bottom-6 right-6 text-white">
                  <div className="overline text-white/70">Capsule 2026 · Vivaha</div>
                  <div className="font-serif text-2xl mt-1 leading-tight">The bridal edit — hand-set, hallmarked, and heirloom-weight.</div>
                </div>
              </motion.div>

              {/* Floating clipped tiles */}
              <motion.div
                initial={{ opacity: 0, y: 40, rotate: -6 }}
                animate={{ opacity: 1, y: 0, rotate: -6 }}
                transition={{ delay: HERO_DELAY + 0.7, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="hidden lg:block absolute -left-16 -bottom-14 w-56 aspect-square rounded-[20px] overflow-hidden shadow-2xl ring-1 ring-black/5"
              >
                <img src={IMG.ring1} alt="Silver ring" className="w-full h-full object-cover" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: -30, rotate: 5 }}
                animate={{ opacity: 1, y: 0, rotate: 5 }}
                transition={{ delay: HERO_DELAY + 0.85, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="hidden lg:block absolute -right-10 -top-6 w-40 aspect-square rounded-[16px] overflow-hidden shadow-xl ring-1 ring-black/5"
              >
                <img src={IMG.earring1} alt="Silver earring" className="w-full h-full object-cover" />
              </motion.div>
            </div>
          </div>

          {/* Scroll hint */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: HERO_DELAY + 1.4, duration: 0.6 }}
            className="mt-20 flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]"
          >
            <span>Scroll</span>
            <span className="h-px w-16 bg-[#C0C0C0]/60 relative overflow-hidden">
              <span className="absolute inset-y-0 -left-full w-full bg-[#1A1A1A] animate-[slide_2.4s_ease-in-out_infinite]" />
            </span>
          </motion.div>
        </div>

        <style>{`@keyframes slide { 0% { transform: translateX(0); } 100% { transform: translateX(200%); } }`}</style>
      </section>

      {/* EDITORIAL MARQUEE — slow, oversized italic tagline strip */}
      <section aria-hidden className="py-10 border-y border-[#EDEDED] bg-white overflow-hidden">
        <div className="marquee">
          <div className="marquee-track">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-16 pr-16 whitespace-nowrap">
                <span className="font-serif italic text-[10vw] lg:text-[7vw] leading-none text-[#1A1A1A]">
                  Timeless silver.
                </span>
                <span className="text-[#C0C0C0] text-4xl">✦</span>
                <span className="font-serif text-[10vw] lg:text-[7vw] leading-none text-[#1A1A1A]/85">
                  Crafted in Pune.
                </span>
                <span className="text-[#C0C0C0] text-4xl">✦</span>
                <span className="font-serif italic text-[10vw] lg:text-[7vw] leading-none text-[#1A1A1A]">
                  Made to be lived in.
                </span>
                <span className="text-[#C0C0C0] text-4xl">✦</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS — numbered, editorial */}
      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline">The house of SIRIJA</div>
            <h2 className="mt-6 font-serif font-light text-4xl lg:text-6xl leading-[1.02] tracking-tight text-[#1A1A1A] max-w-3xl">
              Three quiet principles, kept for years.
            </h2>
          </Reveal>

          <div className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-x-10 gap-y-16">
            {MANIFESTO.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="relative">
                  <div className="font-serif italic text-[120px] lg:text-[160px] leading-none tracking-tighter text-[#1A1A1A]/10 select-none">
                    {c.n}
                  </div>
                  <div className="-mt-10 lg:-mt-14 relative">
                    <h3 className="font-serif text-2xl lg:text-3xl leading-tight tracking-tight text-[#1A1A1A] max-w-sm">{c.title}</h3>
                    <div className="hairline my-6 w-16" />
                    <p className="text-[15px] leading-relaxed text-[#2E2E2E]/75 max-w-sm">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORY MARQUEE */}
      <section className="py-16 border-y border-[#EDEDED]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-10 flex items-end justify-between gap-8 flex-wrap">
          <Reveal>
            <div className="overline">Featured categories</div>
            <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A] max-w-2xl">
              Silver, for every ritual and every day.
            </h2>
          </Reveal>
          <Link to="/collections" data-testid="home-view-all-categories" className="text-xs uppercase tracking-[0.2em] text-[#2E2E2E] hover:text-[#1A1A1A] flex items-center gap-2">
            View all <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </Link>
        </div>
        <div className="marquee">
          <div className="marquee-track">
            {[...CATEGORIES, ...CATEGORIES].map((c, i) => (
              <Link
                key={`${c.slug}-${i}`}
                to="/collections"
                data-testid={`marquee-cat-${c.slug}-${i}`}
                className="shrink-0 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A]/85 hover:text-[#1A1A1A] transition-colors italic tracking-tight"
              >
                {c.name}
                <span className="mx-8 text-[#C0C0C0]">✦</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between gap-6 flex-wrap mb-14">
              <div>
                <div className="overline">Featured pieces</div>
                <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A] max-w-2xl">
                  Selected quietly. <span className="italic">Made deliberately.</span>
                </h2>
              </div>
              <Link to="/shop" data-testid="home-view-all-products" className="text-xs uppercase tracking-[0.2em] text-[#2E2E2E] hover:text-[#1A1A1A] flex items-center gap-2">
                Shop all <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </Link>
            </div>
          </Reveal>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14"
          >
            {featured.map((p) => (
              <motion.div key={p.id} variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } } }}>
                <ProductCard product={p} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE — bento */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center">
              <div className="overline">Why SIRIJA</div>
              <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A] max-w-3xl mx-auto">
                Craft you can weigh in your hand.
              </h2>
              <p className="mt-5 text-[15px] text-[#2E2E2E]/70 leading-relaxed max-w-2xl mx-auto font-light">
                Every piece is hallmarked, hand-finished and quietly guaranteed.
              </p>
            </div>
          </Reveal>
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6"
          >
            {WHY_CHOOSE.map((w, i) => {
              const Icon = ICONS[w.icon] || ShieldCheck;
              return (
                <motion.div
                  key={w.title}
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } }}
                  data-testid={`why-card-${i}`}
                  className="group bg-white p-6 lg:p-8 rounded-2xl border border-[#EDEDED] hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.06)] transition-[transform,box-shadow] duration-500"
                >
                  <div className="h-11 w-11 rounded-full bg-[#F8F9FA] flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" strokeWidth={1.4} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl tracking-tight">{w.title}</h3>
                  <p className="mt-2 text-[13px] text-[#2E2E2E]/70 leading-relaxed">{w.body}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ABOUT PREVIEW w/ mild parallax */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline">Our story</div>
            <h2 className="mt-6 font-serif font-light text-4xl lg:text-5xl leading-tight tracking-tight text-[#1A1A1A]">
              A quiet destination for silver, in the heart of Pune.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[#2E2E2E]/75 max-w-lg font-light">
              SIRIJA is where timeless traditions meet contemporary elegance. From daily wear to bridal sets, from pooja articles to corporate mementos — every piece is selected with obsessive attention to purity, weight and finish.
            </p>
            <Link
              to="/about"
              data-testid="home-about-cta"
              className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1 hover:gap-3 transition-[gap]"
            >
              Read our story <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-6 order-1 lg:order-2" delay={0.15}>
            <div className="relative aspect-[5/4] rounded-2xl overflow-hidden">
              <img src={IMG.store} alt="SIRIJA showroom" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline text-white/50">Loved in Pune</div>
            <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl leading-tight tracking-tight max-w-3xl">
              Read by the people <span className="italic">who wear us.</span>
            </h2>
          </Reveal>
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
            className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.id}
                variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
                data-testid={`testimonial-${i}`}
                className="p-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-0.5 text-[#E5E5E5]">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
                  ))}
                </div>
                <p className="mt-5 font-serif text-lg leading-snug text-white/90">"{t.text}"</p>
                <div className="mt-6 text-sm text-white/80">{t.name}</div>
                <div className="text-xs text-white/50 mt-0.5">{t.location}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <div className="overline">On Instagram</div>
                <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A]">{BRAND.instagramHandle}</h2>
                <p className="mt-4 text-[15px] text-[#2E2E2E]/70 max-w-lg font-light">A weekly note from the studio floor. Tap any tile to open Instagram.</p>
              </div>
              <a href={BRAND.instagram} target="_blank" rel="noreferrer" data-testid="home-ig-link" className="text-xs uppercase tracking-[0.22em] flex items-center gap-2 hover:opacity-80">
                <Instagram className="h-4 w-4" strokeWidth={1.5} /> Follow
              </a>
            </div>
          </Reveal>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            {igPosts.length > 0 ? (
              <InstagramEmbed posts={igPosts} />
            ) : (
              <div data-testid="ig-empty" className="text-center py-16 border border-dashed border-[#E5E5E5] rounded-2xl text-sm text-[#A0A0A0]">
                No Instagram posts yet. <Link to="/admin" className="underline">Add from admin</Link>.
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <div className="overline">Journal</div>
                <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A] max-w-2xl">
                  Notes on silver, styling & tradition.
                </h2>
              </div>
              <Link to="/blog" data-testid="home-blog-link" className="text-xs uppercase tracking-[0.22em] flex items-center gap-2 hover:opacity-80">
                Read all <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {BLOG_POSTS.slice(0, 3).map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08}>
                <Link to="/blog" data-testid={`home-blog-${b.slug}`} className="group block">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden bg-white">
                    <img src={b.image} alt={b.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="mt-5 flex items-center gap-3 text-xs text-[#A0A0A0]">
                    <span>{b.tag}</span><span>·</span><span>{b.date}</span>
                  </div>
                  <h3 className="mt-2 font-serif text-2xl leading-tight tracking-tight group-hover:text-[#1A1A1A]">{b.title}</h3>
                  <p className="mt-2 text-sm text-[#2E2E2E]/70 line-clamp-2">{b.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-24 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <div className="overline">Visit us</div>
            <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-tight">
              691, Raviwar Peth — <span className="italic">step inside our world.</span>
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-[15px] text-[#2E2E2E]/70">
              Consultations, custom orders and quiet browsing — walk in, or write to us and we'll set aside a table.
            </p>
            <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
              <Link to="/contact" data-testid="home-cta-contact" className="btn-fill h-12 px-7 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.22em] hover:text-white flex items-center">Get directions</Link>
              <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} data-testid="home-cta-call" className="h-12 px-7 rounded-full border border-[#1A1A1A]/15 text-xs uppercase tracking-[0.22em] flex items-center hover:bg-[#F8F9FA]">Call {BRAND.phone}</a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
